import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ArtistRemoveItemMutation,
  ArtistRemoveItemMutationVariables,
  ConsoleItemsConnectionFragmentDoc,
  ConsoleItemsQuery,
  ConsoleItemsQueryVariables,
  ItemType,
  PublishItemMutation,
  PublishItemMutationVariables,
  SalesStatus,
  StartSellingItemMutation,
  StartSellingItemMutationVariables,
  StopSellingItemMutation,
  StopSellingItemMutationVariables,
  UnpublishItemMutation,
  UnpublishItemMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useMessage, useTranslation } from '@/core'
import { useCurrentUser } from '@/core/auth'

export type State = ReturnType<typeof useViewModel>['state']
export type Action = ReturnType<typeof useViewModel>['actions']

export const useViewModel = () => {
  const { t } = useTranslation('consoleItems')
  const { showError, showMessage } = useMessage()
  const { user } = useCurrentUser()
  const { data, fetchMore, loading, refetch } = useQuery<
    ConsoleItemsQuery,
    ConsoleItemsQueryVariables
  >(QUERY, { notifyOnNetworkStatusChange: true })

  const [publishItem] = useMutation<
    PublishItemMutation,
    PublishItemMutationVariables
  >(PUBLIC_ITEM)

  const [unpublishItem] = useMutation<
    UnpublishItemMutation,
    UnpublishItemMutationVariables
  >(UNPUBLIC_ITEM)

  const [startSellingItem] = useMutation<
    StartSellingItemMutation,
    StartSellingItemMutationVariables
  >(START_SELLING_ITEM)

  const [stopSellingItem] = useMutation<
    StopSellingItemMutation,
    StopSellingItemMutationVariables
  >(STOP_SELLING_ITEM)

  const [artistRemoveItem] = useMutation<
    ArtistRemoveItemMutation,
    ArtistRemoveItemMutationVariables
  >(ARTIST_REMOVE_ITEM)

  return {
    state: {
      user,
      items: data?.artistItems ?? null,
      loading,
      artist: data?.artist ?? null,
      hasNextPage: !!data?.artistItems?.pageInfo?.hasNextPage,
      artistId: user?.uid ?? '',
      membership: data?.artist?.subscription?.products ?? undefined,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            pageInput: {
              cursor: data?.artistItems?.pageInfo.endCursor,
            },
          },
        })
      },
      async onFilter(filter: ItemType | 'general') {
        refetch({
          filters: {
            type: filter === 'general' ? null : filter,
          },
        })
      },
      async onRemove(id: string) {
        await artistRemoveItem({ variables: { id } })
          .then(() => {
            showMessage(t('deletedItem'), 'success')
          })
          .catch(error => showError(error))
      },
      async onSearch(searchTerm: string) {
        refetch({
          filters: {
            searchTerm: searchTerm,
          },
        })
      },
      async onToggleActive(id: string, isActive: boolean) {
        if (isActive) {
          await unpublishItem({
            variables: { id },
            optimisticResponse() {
              return {
                unpublishItem: {
                  id,
                  isPublished: false,
                  __typename: 'Item',
                },
              }
            },
          }).catch(showError)
          return
        }
        await publishItem({
          variables: { id },
          optimisticResponse() {
            return {
              publishItem: {
                id,
                isPublished: true,
                __typename: 'Item',
              },
            }
          },
        }).catch(showError)
      },
      async onToggleSelling(id: string, isSelling: boolean) {
        if (isSelling) {
          await stopSellingItem({
            variables: { id },
            optimisticResponse() {
              return {
                stopSellingItem: {
                  id,
                  salesStatus: SalesStatus.Stopped,
                  __typename: 'Item',
                },
              }
            },
          }).catch(showError)
          return
        }
        await startSellingItem({
          variables: { id },
          optimisticResponse() {
            return {
              startSellingItem: {
                id,
                salesStatus: SalesStatus.Active,
                __typename: 'Item',
              },
            }
          },
        }).catch(showError)
      },
    },
  }
}

const QUERY = gql`
  query ConsoleItems($filters: ArtistItemsFilterInput, $pageInput: PageInput) {
    artistItems(filters: $filters, pageInput: $pageInput) {
      ...ConsoleItemsConnection
    }
    artist {
      id
      paymentType
      hasArrangingDeliveryItems
      subscription {
        products {
          productId
        }
      }
    }
  }
  ${ConsoleItemsConnectionFragmentDoc}
`

const PUBLIC_ITEM = gql`
  mutation PublishItem($id: ID!) {
    publishItem(id: $id) {
      id
      isPublished
    }
  }
`

const UNPUBLIC_ITEM = gql`
  mutation UnpublishItem($id: ID!) {
    unpublishItem(id: $id) {
      id
      isPublished
    }
  }
`

const START_SELLING_ITEM = gql`
  mutation StartSellingItem($id: ID!) {
    startSellingItem(id: $id) {
      id
      salesStatus
    }
  }
`

const STOP_SELLING_ITEM = gql`
  mutation StopSellingItem($id: ID!) {
    stopSellingItem(id: $id) {
      id
      salesStatus
    }
  }
`

const ARTIST_REMOVE_ITEM = gql`
  mutation ArtistRemoveItem($id: ID!) {
    artistRemoveItem(id: $id) {
      id
    }
  }
`
